export class VoiceService {
    private recognition: any;
    private synth: SpeechSynthesis;
    private isListening: boolean = false;
    private fullTranscript: string = "";
    private onResultCallback: ((text: string) => void) | null = null;
    private silenceTimer: any = null;
    private SILENCE_THRESHOLD = 1500; // 1.5 seconds

    constructor() {
        this.synth = window.speechSynthesis;
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        
        if (SpeechRecognition) {
            this.recognition = new SpeechRecognition();
            this.recognition.continuous = true;
            this.recognition.interimResults = true;
            this.recognition.lang = 'en-US';

            this.recognition.onresult = (event: any) => {
                let interimTranscript = '';

                // Iterate through all current results to build the accurate transcript
                // SpeechRecognition with continuous=true may have multiple results.
                for (let i = event.resultIndex; i < event.results.length; ++i) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        this.fullTranscript += transcript;
                    } else {
                        interimTranscript += transcript;
                    }
                }

                const displayTranscript = (this.fullTranscript + interimTranscript).trim();

                if (displayTranscript) {
                    this._resetSilenceTimer(displayTranscript);
                    
                    // Dispatch entire combined transcript for AVI captions
                    window.dispatchEvent(new CustomEvent('voice-interim-result', { 
                        detail: { text: displayTranscript } 
                    }));
                }
            };

            this.recognition.onsoundstart = () => {
                // User started speaking - interrupt AI if it's talking
                if (this.synth.speaking) {
                    this.synth.cancel();
                    window.dispatchEvent(new CustomEvent('avi-interrupted'));
                }
            };

            this.recognition.onend = () => {
                if (this.isListening) {
                    try {
                        this.recognition.start(); // Keep listening in continuous mode
                    } catch(e) {}
                }
            };

            this.recognition.onerror = (event: any) => {
                console.error('Speech recognition error', event.error);
                if (event.error === 'not-allowed') {
                    this.isListening = false;
                    window.dispatchEvent(new CustomEvent('voice-listening-ended'));
                }
            };
        }

        // Pre-load voices so they are ready when speak() is first called
        if (this.synth.getVoices().length === 0) {
            this.synth.addEventListener('voiceschanged', () => { this.synth.getVoices(); });
        }
    }

    private _resetSilenceTimer(text: string) {
        if (this.silenceTimer) clearTimeout(this.silenceTimer);
        
        this.silenceTimer = setTimeout(() => {
            if (this.onResultCallback && text.trim()) {
                this.onResultCallback(text);
                // Important: Clear the persistent buffer after sending a complete phrase
                this.fullTranscript = "";
            }
        }, this.SILENCE_THRESHOLD);
    }

    public startListening(callback: (text: string) => void) {
        if (!this.recognition) {
            alert('Speech recognition not supported in this browser.');
            return;
        }

        this.onResultCallback = callback;
        try {
            if (!this.isListening) {
                this.recognition.start();
                this.isListening = true;
            }
        } catch (e) {
            console.error('Failed to start recognition', e);
        }
    }

    public stopListening() {
        if (this.recognition && this.isListening) {
            this.isListening = false;
            this.recognition.stop();
            if (this.silenceTimer) clearTimeout(this.silenceTimer);
        }
    }

    public setSilenceThreshold(ms: number) {
        this.SILENCE_THRESHOLD = ms;
    }

    public speak(text: string, options: { voiceName?: string, rate?: number, pitch?: number, volume?: number } = {}) {
        // Chrome bug: calling speak() immediately after cancel() silently fails.
        // Fix: cancel first, then wait 120ms before queuing the new utterance.
        this.synth.cancel();

        const _doSpeak = () => {
            const cleanText = text.replace(/[*#`[\]]/g, '').trim();
            if (!cleanText) return;

            const utterance = new SpeechSynthesisUtterance(cleanText);

            const voices = this.synth.getVoices();
            
            // 1. Try specified voice name
            if (options.voiceName) {
                const chosen = voices.find(v => v.name === options.voiceName);
                if (chosen) utterance.voice = chosen;
            } 
            
            // 2. Fallback to preferred list if no voice yet
            if (!utterance.voice) {
                const preferred = voices.find(v =>
                    v.name.includes('Google') ||
                    v.name.includes('Premium') ||
                    v.name.includes('Natural') ||
                    v.name.includes('Samantha') ||
                    v.name.includes('Karen')
                );
                if (preferred) utterance.voice = preferred;
            }

            utterance.rate   = options.rate || 1.05;
            utterance.pitch  = options.pitch || 1.0;
            utterance.volume = options.volume || 1.0;

            utterance.onstart = () => { window.dispatchEvent(new CustomEvent('voice-speak-start')); };
            utterance.onend   = () => { window.dispatchEvent(new CustomEvent('voice-speak-end')); };
            utterance.onerror = (e) => { console.error('[VoiceService] TTS error:', e); };

            this.synth.speak(utterance);
        };

        setTimeout(_doSpeak, 120);
    }


    public cancelSpeech() {
        this.synth.cancel();
    }

    public isSpeaking(): boolean {
        return this.synth.speaking;
    }
}

export const voiceService = new VoiceService();
